import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { AIThinkingState } from "@/components/AltioraComponents";
import { PROFESSIONALS } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Users, MapPin, Star, Video, Phone, Building2, ChevronRight, X, Sparkles,
  BadgeCheck, Compass, PiggyBank, Scale, Briefcase, Stamp, Calculator, Brain,
  UserSearch, Home, HeartHandshake, Award, Languages, DollarSign, Clock, CheckCircle2,
} from "lucide-react";

const SPECIALTY_ICONS = {
  financial_advisor: PiggyBank,
  career_coach: Compass,
  lawyer_immigration: Scale,
  lawyer_business: Briefcase,
  notary: Stamp,
  accountant: Calculator,
  psychologist: Brain,
  recruiter: UserSearch,
  mortgage_broker: Home,
  community_mentor: HeartHandshake,
};

const MODALITY_META = {
  in_person: { icon: MapPin, fr: "Présentiel", en: "In person" },
  video: { icon: Video, fr: "Visio", en: "Video" },
  phone: { icon: Phone, fr: "Téléphone", en: "Phone" },
};

const LANG_LABEL = {
  fr: "Français", en: "English", es: "Español", ar: "العربية", zh: "中文", hi: "हिन्दी", ur: "اردو", pa: "ਪੰਜਾਬੀ", de: "Deutsch", wo: "Wolof",
};

const DEFAULT_MTL = { lat: 45.5017, lng: -73.5673 };

export default function Professionals() {
  const { lang } = useI18n();
  const [specialties, setSpecialties] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [specialty, setSpecialty] = useState("");
  const [language, setLanguage] = useState("");
  const [modality, setModality] = useState("");
  const [freeFirst, setFreeFirst] = useState(false);
  const [center, setCenter] = useState(DEFAULT_MTL);
  const [radius, setRadius] = useState(50);
  const [query, setQuery] = useState("");

  // Selected pro drawer
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Booking dialog
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingModality, setBookingModality] = useState("video");
  const [bookingSlots, setBookingSlots] = useState([]);
  const [bookingTopic, setBookingTopic] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // My bookings tab
  const [tab, setTab] = useState("search"); // "search" | "mine"
  const [myBookings, setMyBookings] = useState([]);

  // Load specialties on first mount (seed is invoked server-side on boot in production;
  // we no longer call POST /seed here to avoid a redundant network round-trip per session).
  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/professionals/specialties");
        setSpecialties(s.data.specialties || []);
        setModalities(s.data.modalities || []);
      } catch { /* silent */ }
    })();
    // Try to get user's real location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 5000 },
      );
    }
  }, []);

  // Fetch results whenever filters change
  useEffect(() => {
    if (tab !== "search") return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/professionals/search", {
          lat: center?.lat,
          lng: center?.lng,
          radius_km: radius,
          specialty: specialty || null,
          language: language || null,
          modality: modality || null,
          free_first_session: freeFirst ? true : null,
          query: query || null,
        });
        if (!cancelled) setItems(data.items || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tab, specialty, language, modality, freeFirst, radius, query, center?.lat, center?.lng]);

  // Fetch bookings when switching tabs
  useEffect(() => {
    if (tab !== "mine") return;
    (async () => {
      try {
        const { data } = await api.get("/professionals/bookings/mine");
        setMyBookings(data.items || []);
      } catch { /* silent */ }
    })();
  }, [tab]);

  const specialtyMap = useMemo(() => {
    const m = {};
    specialties.forEach((s) => (m[s.key] = s));
    return m;
  }, [specialties]);

  const openDetail = async (pro) => {
    setSelected(pro);
    setDetailLoading(true);
    try {
      const params = new URLSearchParams({ lat: String(center?.lat || 0), lng: String(center?.lng || 0) });
      const { data } = await api.get(`/professionals/${pro.key}?${params.toString()}`);
      setSelected(data);
    } catch { /* silent */ }
    finally { setDetailLoading(false); }
  };

  const startBooking = () => {
    if (!selected) return;
    // Pre-select first available modality that pro supports
    const preferred = selected.modalities?.includes("video") ? "video" : selected.modalities?.[0] || "video";
    setBookingModality(preferred);
    setBookingSlots([]);
    setBookingTopic("");
    setBookingOpen(true);
  };

  const toggleSlot = (iso) => {
    setBookingSlots((prev) => {
      if (prev.includes(iso)) return prev.filter((s) => s !== iso);
      if (prev.length >= 3) {
        toast.info(lang === "fr" ? "Maximum 3 créneaux — désélectionnez pour changer." : "Max 3 slots — deselect to swap.");
        return prev;
      }
      return [...prev, iso];
    });
  };

  const submitBooking = async () => {
    if (!selected || bookingSlots.length === 0 || bookingTopic.trim().length < 8) {
      toast.error(lang === "fr" ? "Choisissez au moins 1 créneau et décrivez votre besoin (≥ 8 caractères)." : "Choose at least 1 slot and describe your need (≥ 8 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/professionals/${selected.key}/book`, {
        modality: bookingModality,
        proposed_slots: bookingSlots,
        topic_fr: bookingTopic,
      });
      toast.success(data[`message_${lang}`] || (lang === "fr" ? "Demande envoyée." : "Request sent."));
      setBookingOpen(false);
      setSelected(null);
    } catch (e) {
      toast.error(lang === "fr" ? "Échec — réessayez." : "Failed — please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full" data-testid={PROFESSIONALS.root}>
      <TopBar
        title={lang === "fr" ? "Parler à un professionnel" : "Talk to a professional"}
        subtitle={lang === "fr"
          ? "Conseillers, avocats, psychologues, notaires… recommandés automatiquement selon votre trajectoire ALTIORA."
          : "Advisors, attorneys, psychologists, notaries… auto-recommended based on your ALTIORA trajectory."}
      />

      {/* Tabs */}
      <div className="px-6 md:px-10 pt-6 max-w-[1800px] mx-auto">
        <div className="inline-flex rounded-md border border-[#E4E1D8] bg-white overflow-hidden">
          <button
            data-testid="professionals-tab-search"
            onClick={() => setTab("search")}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest altiora-transition ${
              tab === "search" ? "bg-[#0B2545] text-white" : "text-[#5B5B62] hover:text-[#1F2937]"
            }`}
          >
            {lang === "fr" ? "Trouver un pro" : "Find a pro"}
          </button>
          <button
            data-testid={PROFESSIONALS.myBookings}
            onClick={() => setTab("mine")}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest altiora-transition ${
              tab === "mine" ? "bg-[#0B2545] text-white" : "text-[#5B5B62] hover:text-[#1F2937]"
            }`}
          >
            {lang === "fr" ? `Mes RDV (${myBookings.length})` : `My bookings (${myBookings.length})`}
          </button>
        </div>
      </div>

      {tab === "search" ? (
        <div className="px-6 md:px-10 py-6 max-w-[1800px] mx-auto">
          <div className="grid grid-cols-12 gap-6">
            {/* ============ Filters ============ */}
            <aside className="col-span-12 lg:col-span-3 space-y-4 altiora-fade-up">
              <div className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                  {lang === "fr" ? "Recherche libre" : "Free search"}
                </div>
                <input
                  data-testid="professionals-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={lang === "fr" ? "immigration, budget, burnout…" : "immigration, budget, burnout…"}
                  className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm text-[#1F2937] placeholder:text-[#8B8B8F] focus:outline-none focus:border-[#0B2545] altiora-transition"
                />
              </div>

              <div data-testid={PROFESSIONALS.specialtyFilter} className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                  {lang === "fr" ? "Spécialité" : "Specialty"}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setSpecialty("")}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                      !specialty ? "bg-[#0B2545] border-[#0B2545] text-white" : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >
                    {lang === "fr" ? "Tous" : "All"}
                  </button>
                  {specialties.map((s) => {
                    const Ico = SPECIALTY_ICONS[s.key] || Users;
                    const active = specialty === s.key;
                    return (
                      <button
                        key={s.key}
                        data-testid={`professionals-specialty-${s.key}`}
                        onClick={() => setSpecialty(active ? "" : s.key)}
                        className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                          active ? "bg-[#0B2545]/15 border-[#0B2545] text-[#0B2545]" : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                        }`}
                      >
                        <Ico className="h-3 w-3" strokeWidth={2} />
                        {s[`label_${lang}`]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div data-testid={PROFESSIONALS.modalityFilter} className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                  {lang === "fr" ? "Modalité" : "Modality"}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setModality("")}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                      !modality ? "bg-[#0B2545] border-[#0B2545] text-white" : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >
                    {lang === "fr" ? "Toutes" : "All"}
                  </button>
                  {modalities.map((m) => {
                    const meta = MODALITY_META[m] || { icon: Video, fr: m, en: m };
                    const Ico = meta.icon;
                    const active = modality === m;
                    return (
                      <button
                        key={m}
                        data-testid={`professionals-modality-${m}`}
                        onClick={() => setModality(active ? "" : m)}
                        className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                          active ? "bg-[#0B2545]/15 border-[#0B2545] text-[#0B2545]" : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                        }`}
                      >
                        <Ico className="h-3 w-3" strokeWidth={2} />
                        {meta[lang]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div data-testid={PROFESSIONALS.languageFilter} className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                  {lang === "fr" ? "Langue" : "Language"}
                </div>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm text-[#1F2937] focus:outline-none focus:border-[#0B2545]"
                >
                  <option value="">{lang === "fr" ? "Toutes" : "All"}</option>
                  {Object.keys(LANG_LABEL).map((code) => (
                    <option key={code} value={code}>{LANG_LABEL[code]}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                  {lang === "fr" ? "Rayon" : "Radius"}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[10, 25, 50, 100, 500].map((r) => (
                    <button
                      key={r}
                      onClick={() => setRadius(r)}
                      className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                        radius === r ? "bg-[#0B2545] border-[#0B2545] text-white" : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                      }`}
                    >{r} km</button>
                  ))}
                  <button
                    data-testid="professionals-radius-worldwide"
                    onClick={() => setRadius(20000)}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                      radius === 20000 ? "bg-[#0B2545] border-[#0B2545] text-white" : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >{lang === "fr" ? "Monde" : "Worldwide"}</button>
                </div>
              </div>

              <label data-testid={PROFESSIONALS.freeFirstFilter}
                className="flex items-center gap-3 rounded-xl border border-[#E4E1D8] bg-white p-4 shadow-sm cursor-pointer hover:border-[#B8B5AC] altiora-transition">
                <input
                  type="checkbox"
                  checked={freeFirst}
                  onChange={(e) => setFreeFirst(e.target.checked)}
                  className="h-4 w-4 accent-[#0B2545]"
                />
                <div>
                  <div className="text-sm text-[#1F2937]">
                    {lang === "fr" ? "1ère séance offerte" : "1st session free"}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                    {lang === "fr" ? "Sans engagement" : "No commitment"}
                  </div>
                </div>
              </label>

              <div className="rounded-xl border border-[#C5A880]/40 bg-gradient-to-b from-[#FBF6EA] to-white p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.6} />
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7A5B14]">
                    Trusted Intelligence™
                  </div>
                </div>
                <div className="text-xs text-[#5B5B62] leading-relaxed">
                  {lang === "fr"
                    ? "Chaque recommandation est expliquée : rating, distance, langue, alignement avec votre profil ALTIORA."
                    : "Every recommendation is explained: rating, distance, language, alignment with your ALTIORA profile."}
                </div>
              </div>
            </aside>

            {/* ============ Results ============ */}
            <section className="col-span-12 lg:col-span-9">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl border border-[#E4E1D8] bg-white p-6 shadow-sm">
                      <AIThinkingState lang={lang} />
                    </div>
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="rounded-xl border border-[#E4E1D8] bg-white p-12 text-center shadow-sm">
                  <Users className="h-8 w-8 text-[#8B8B8F] mx-auto" strokeWidth={1.5} />
                  <div className="mt-4 font-display text-lg text-[#1F2937]">
                    {lang === "fr" ? "Aucun professionnel trouvé" : "No professional found"}
                  </div>
                  <div className="mt-2 text-sm text-[#5B5B62]">
                    {lang === "fr" ? "Élargissez le rayon ou modifiez les filtres." : "Widen the radius or adjust filters."}
                  </div>
                  <button
                    onClick={() => { setRadius(20000); setSpecialty(""); setLanguage(""); setModality(""); setFreeFirst(false); setQuery(""); }}
                    className="mt-5 inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition shadow-sm"
                  >
                    {lang === "fr" ? "Rechercher dans le monde entier" : "Search worldwide"}
                    <ChevronRight className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 altiora-stagger">
                  {items.map((p) => (
                    <ProfessionalCard key={p.key} pro={p} lang={lang} specialtyMap={specialtyMap} onOpen={() => openDetail(p)} />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      ) : (
        <BookingsList items={myBookings} lang={lang} />
      )}

      {/* ============ Detail Drawer ============ */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="professionals-detail-drawer">
          <div className="absolute inset-0 bg-[#1F2937]/40 backdrop-blur-sm altiora-fade-in" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg h-full bg-[#F8F7F3] border-l border-[#E4E1D8] flex flex-col altiora-slide-in shadow-2xl">
            <ProDetailHeader pro={selected} lang={lang} specialtyMap={specialtyMap} onClose={() => setSelected(null)} />
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {detailLoading && <AIThinkingState lang={lang} />}

              <Section title={lang === "fr" ? "Pourquoi ce match" : "Why this match"}>
                <div className="rounded-lg border border-[#C5A880]/40 bg-[#FBF6EA] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">
                      {lang === "fr" ? "Score de correspondance" : "Match score"}
                    </div>
                    <div className="font-display text-lg text-[#7A5B14]">{selected.match_score}/100</div>
                  </div>
                  <ul className="space-y-1.5">
                    {(selected.match_reasons || []).map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1F2937]">
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#059669] flex-shrink-0" strokeWidth={2} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>

              <Section title={lang === "fr" ? "Biographie" : "Bio"}>
                <div className="text-sm text-[#1F2937] leading-relaxed">
                  {selected[`bio_${lang}`] || selected.bio_fr}
                </div>
              </Section>

              <Section title={lang === "fr" ? "Modalités & tarifs" : "Modalities & fees"}>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-md border border-[#E4E1D8] bg-white p-3">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">
                      {lang === "fr" ? "Tarif séance" : "Session fee"}
                    </div>
                    <div className="mt-1 font-display text-lg text-[#1F2937]">
                      {selected.session_fee_cad ? `${selected.session_fee_cad} $` : (lang === "fr" ? "Gratuit" : "Free")}
                    </div>
                  </div>
                  <div className="rounded-md border border-[#E4E1D8] bg-white p-3">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">
                      {lang === "fr" ? "1ère séance" : "1st session"}
                    </div>
                    <div className={`mt-1 font-display text-lg ${selected.first_session_free ? "text-[#059669]" : "text-[#1F2937]"}`}>
                      {selected.first_session_free ? (lang === "fr" ? "Offerte" : "Free") : "—"}
                    </div>
                  </div>
                  <div className="rounded-md border border-[#E4E1D8] bg-white p-3">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">
                      {lang === "fr" ? "Distance" : "Distance"}
                    </div>
                    <div className="mt-1 font-display text-lg text-[#1F2937]">
                      {selected.distance_km !== null && selected.distance_km !== undefined ? `${selected.distance_km.toFixed(1)} km` : "—"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(selected.modalities || []).map((m) => {
                    const meta = MODALITY_META[m] || { icon: Video, fr: m, en: m };
                    const Ico = meta.icon;
                    return (
                      <span key={m} className="inline-flex items-center gap-1.5 rounded-full border border-[#0B2545]/25 bg-[#0B2545]/5 px-3 py-1 text-[11px] text-[#0B2545]">
                        <Ico className="h-3 w-3" strokeWidth={2} />
                        {meta[lang]}
                      </span>
                    );
                  })}
                </div>
              </Section>

              <Section title={lang === "fr" ? "Langues" : "Languages"}>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.languages || []).map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 rounded border border-[#E4E1D8] bg-white px-2 py-0.5 text-[11px] text-[#5B5B62]">
                      <Languages className="h-3 w-3" strokeWidth={2} />
                      {LANG_LABEL[l] || l}
                    </span>
                  ))}
                </div>
              </Section>

              <Section title={lang === "fr" ? "Qualifications" : "Credentials"}>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.credentials || []).map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 rounded border border-[#C5A880]/40 bg-[#FBF6EA] px-2 py-0.5 text-[11px] text-[#7A5B14]">
                      <Award className="h-3 w-3" strokeWidth={2} />
                      {c}
                    </span>
                  ))}
                  {selected.verified && (
                    <span className="inline-flex items-center gap-1 rounded border border-[#059669]/40 bg-[#ECFDF5] px-2 py-0.5 text-[11px] text-[#065F46]">
                      <BadgeCheck className="h-3 w-3" strokeWidth={2} />
                      {lang === "fr" ? "Vérifié ALTIORA" : "ALTIORA-verified"}
                    </span>
                  )}
                </div>
              </Section>

              <Section title="Trusted Intelligence™">
                <div className="rounded-md border border-[#E4E1D8] bg-white p-4 space-y-2 text-xs">
                  <Row label={lang === "fr" ? "Données utilisées" : "Data used"} value={(selected.trust?.data_used || []).join(", ")} />
                  <Row label={lang === "fr" ? "Hypothèses" : "Assumptions"} value={(selected.trust?.assumptions || []).join(", ")} />
                  <Row label={lang === "fr" ? "Confiance" : "Confidence"} value={`${Math.round((selected.trust?.confidence || 0) * 100)}%`} />
                  <Row label={lang === "fr" ? "Aide à la décision uniquement" : "Decision-support only"} value={lang === "fr" ? "Oui — jamais une garantie." : "Yes — never a guarantee."} accent />
                </div>
              </Section>
            </div>

            <div className="border-t border-[#E4E1D8] bg-white px-6 py-4 flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-1 text-[#C5A880]">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-3.5 w-3.5 ${n <= Math.round(selected.rating || 0) ? "fill-[#C5A880]" : "opacity-30"}`} strokeWidth={1.5} />
                  ))}
                  <span className="ml-1 text-xs text-[#1F2937] font-medium">{selected.rating?.toFixed(1)}</span>
                  <span className="ml-1 text-[10px] text-[#8B8B8F]">({selected.reviews_count})</span>
                </div>
              </div>
              <button
                data-testid={PROFESSIONALS.bookBtn}
                onClick={startBooking}
                className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition shadow-sm hover:shadow-md"
              >
                {lang === "fr" ? "Demander un RDV" : "Book a session"}
                <ChevronRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ Booking Dialog ============ */}
      {bookingOpen && selected && (
        <BookingDialog
          pro={selected}
          lang={lang}
          modality={bookingModality}
          setModality={setBookingModality}
          slots={bookingSlots}
          toggleSlot={toggleSlot}
          topic={bookingTopic}
          setTopic={setBookingTopic}
          submitting={submitting}
          onClose={() => setBookingOpen(false)}
          onSubmit={submitBooking}
        />
      )}
    </div>
  );
}

/* ============ Sub-components ============ */

function ProfessionalCard({ pro, lang, specialtyMap, onOpen }) {
  const Ico = SPECIALTY_ICONS[pro.specialty] || Users;
  const spec = specialtyMap[pro.specialty] || {};
  return (
    <button
      data-testid={PROFESSIONALS.card}
      onClick={onOpen}
      className="text-left rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm hover:border-[#B8B5AC] hover:shadow-md hover:-translate-y-0.5 altiora-transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 rounded-full bg-[#F1EFE9] border border-[#E4E1D8] flex items-center justify-center flex-shrink-0">
            <span className="font-display text-lg text-[#0B2545]">
              {pro.first_name?.[0]}{pro.last_name?.[0]}
            </span>
          </div>
          <div className="min-w-0">
            <div className="font-display text-base text-[#1F2937] tracking-tight truncate">
              {pro.first_name} {pro.last_name}
            </div>
            <div className="mt-0.5 text-xs text-[#5B5B62] truncate">{pro[`title_${lang}`] || pro.title_fr}</div>
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545]">
              <Ico className="h-3 w-3" strokeWidth={2} />
              {spec[`label_${lang}`] || pro.specialty}
            </div>
          </div>
        </div>
        {pro.featured && (
          <span className="inline-flex items-center gap-1 rounded-full border border-[#C5A880]/50 bg-[#FBF6EA] px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-[#7A5B14] flex-shrink-0">
            <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
            {lang === "fr" ? "Recommandé" : "Featured"}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="flex items-center gap-3 text-[#5B5B62]">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" strokeWidth={2} />
            {pro.city}
            {pro.distance_km !== null && pro.distance_km !== undefined && (
              <span className="text-[#8B8B8F]"> · {pro.distance_km.toFixed(1)} km</span>
            )}
          </span>
          <span className="inline-flex items-center gap-1 text-[#C5A880]">
            <Star className="h-3 w-3 fill-[#C5A880]" strokeWidth={1.5} />
            <span className="text-[#1F2937] font-medium">{pro.rating?.toFixed(1)}</span>
            <span className="text-[#8B8B8F]">({pro.reviews_count})</span>
          </span>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545]">
          {lang === "fr" ? "Match" : "Match"} {pro.match_score}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {(pro.modalities || []).slice(0, 3).map((m) => {
          const meta = MODALITY_META[m] || { icon: Video, fr: m, en: m };
          const MIco = meta.icon;
          return (
            <span key={m} className="inline-flex items-center gap-1 rounded-full border border-[#E4E1D8] bg-[#F8F7F3] px-2 py-0.5 text-[10px] text-[#5B5B62]">
              <MIco className="h-2.5 w-2.5" strokeWidth={2} />
              {meta[lang]}
            </span>
          );
        })}
        {pro.first_session_free && (
          <span className="inline-flex items-center gap-1 rounded-full border border-[#059669]/40 bg-[#ECFDF5] px-2 py-0.5 text-[10px] text-[#065F46]">
            <DollarSign className="h-2.5 w-2.5" strokeWidth={2} />
            {lang === "fr" ? "1ère offerte" : "1st free"}
          </span>
        )}
      </div>

      {(pro.match_reasons || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E4E1D8]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] mb-1">
            {lang === "fr" ? "Pourquoi ce pro" : "Why this pro"}
          </div>
          <div className="text-xs text-[#5B5B62] line-clamp-2">
            {pro.match_reasons.slice(0, 2).join(" · ")}
          </div>
        </div>
      )}
    </button>
  );
}

function ProDetailHeader({ pro, lang, specialtyMap, onClose }) {
  const Ico = SPECIALTY_ICONS[pro.specialty] || Users;
  const spec = specialtyMap[pro.specialty] || {};
  return (
    <div className="px-6 pt-5 pb-4 border-b border-[#E4E1D8] bg-white">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-full bg-[#F1EFE9] border border-[#E4E1D8] flex items-center justify-center flex-shrink-0">
            <span className="font-display text-xl text-[#0B2545]">
              {pro.first_name?.[0]}{pro.last_name?.[0]}
            </span>
          </div>
          <div>
            <div className="font-display text-xl text-[#1F2937] tracking-tight">
              {pro.first_name} {pro.last_name}
            </div>
            <div className="mt-0.5 text-sm text-[#5B5B62]">{pro[`title_${lang}`] || pro.title_fr}</div>
            <div className="mt-1.5 inline-flex items-center gap-3">
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545]">
                <Ico className="h-3 w-3" strokeWidth={2} />
                {spec[`label_${lang}`] || pro.specialty}
              </span>
              {pro.org_name && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                  <Building2 className="h-3 w-3" strokeWidth={2} />
                  {pro.org_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 text-[#5B5B62] hover:text-[#1F2937]">
          <X className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-2">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-1 font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">{label}</div>
      <div className={`col-span-2 ${accent ? "text-[#0B2545] font-medium" : "text-[#1F2937]"}`}>{value || "—"}</div>
    </div>
  );
}

function BookingDialog({ pro, lang, modality, setModality, slots, toggleSlot, topic, setTopic, submitting, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid={PROFESSIONALS.bookDialog}>
      <div className="absolute inset-0 bg-[#1F2937]/50 backdrop-blur-sm altiora-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-xl border border-[#E4E1D8] shadow-2xl altiora-scale-in max-h-[90vh] flex flex-col">
        <div className="px-6 py-5 border-b border-[#E4E1D8] flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
              {lang === "fr" ? "Demande de rendez-vous" : "Appointment request"}
            </div>
            <div className="mt-1 font-display text-lg text-[#1F2937]">
              {lang === "fr" ? "avec" : "with"} {pro.first_name} {pro.last_name}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#5B5B62] hover:text-[#1F2937]">
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Modality */}
          <div data-testid={PROFESSIONALS.bookModality}>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-2">
              {lang === "fr" ? "Modalité souhaitée" : "Preferred modality"}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(pro.modalities || []).map((m) => {
                const meta = MODALITY_META[m] || { icon: Video, fr: m, en: m };
                const Ico = meta.icon;
                const active = modality === m;
                return (
                  <button
                    key={m}
                    data-testid={`professionals-book-modality-${m}`}
                    onClick={() => setModality(m)}
                    className={`inline-flex flex-col items-center gap-1 py-3 rounded-md border altiora-transition ${
                      active ? "bg-[#0B2545] border-[#0B2545] text-white" : "bg-[#F8F7F3] border-[#E4E1D8] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >
                    <Ico className="h-4 w-4" strokeWidth={1.6} />
                    <span className="text-[11px] font-medium">{meta[lang]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Slots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                {lang === "fr" ? "Proposez 1 à 3 créneaux" : "Propose 1 to 3 slots"}
              </div>
              <div className="text-[10px] font-mono text-[#8B8B8F]">{slots.length}/3</div>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-[240px] overflow-y-auto pr-1">
              {(pro.availability_slots || []).map((s) => {
                const dt = new Date(s.start_utc);
                const active = slots.includes(s.start_utc);
                return (
                  <button
                    key={s.start_utc}
                    data-testid={PROFESSIONALS.bookSlot}
                    onClick={() => toggleSlot(s.start_utc)}
                    className={`rounded-md border px-3 py-2.5 text-left altiora-transition ${
                      active ? "bg-[#0B2545]/8 border-[#0B2545]" : "bg-[#F8F7F3] border-[#E4E1D8] hover:border-[#B8B5AC]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className={`h-3 w-3 ${active ? "text-[#0B2545]" : "text-[#8B8B8F]"}`} strokeWidth={2} />
                      <div className="text-xs font-medium text-[#1F2937]">
                        {dt.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { weekday: "short", day: "2-digit", month: "short" })}
                      </div>
                    </div>
                    <div className="mt-1 text-[10px] font-mono text-[#5B5B62]">
                      {dt.toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" })} · {s.duration_min} min
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Topic */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-2">
              {lang === "fr" ? "Décrivez votre besoin" : "Describe your need"}
            </div>
            <textarea
              data-testid={PROFESSIONALS.bookTopic}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={4}
              placeholder={lang === "fr"
                ? "Ex : Je souhaite structurer mon budget avant l'achat d'une première propriété…"
                : "E.g.: I want to structure my budget before a first home purchase…"}
              className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm text-[#1F2937] placeholder:text-[#8B8B8F] focus:outline-none focus:border-[#0B2545] altiora-transition"
            />
            <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
              {lang === "fr" ? "Le pro reçoit ce résumé + votre profil ALTIORA (avec votre accord)." : "The pro receives this summary + your ALTIORA profile (with your consent)."}
            </div>
          </div>
        </div>

        <div className="border-t border-[#E4E1D8] bg-[#F8F7F3] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm text-[#5B5B62] hover:text-[#1F2937] px-4 py-2 rounded-md altiora-transition">
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
          <button
            data-testid={PROFESSIONALS.bookSubmit}
            onClick={onSubmit}
            disabled={submitting || slots.length === 0 || topic.trim().length < 8}
            className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (lang === "fr" ? "Envoi…" : "Sending…") : (lang === "fr" ? "Envoyer la demande" : "Send request")}
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

function BookingsList({ items, lang }) {
  return (
    <div className="px-6 md:px-10 py-6 max-w-[1200px] mx-auto">
      {items.length === 0 ? (
        <div className="rounded-xl border border-[#E4E1D8] bg-white p-12 text-center shadow-sm">
          <Users className="h-8 w-8 text-[#8B8B8F] mx-auto" strokeWidth={1.5} />
          <div className="mt-4 font-display text-lg text-[#1F2937]">
            {lang === "fr" ? "Aucun rendez-vous pour l'instant" : "No bookings yet"}
          </div>
          <div className="mt-2 text-sm text-[#5B5B62]">
            {lang === "fr" ? "Explorez les professionnels et faites votre première demande." : "Explore professionals and make your first request."}
          </div>
        </div>
      ) : (
        <div className="space-y-3 altiora-stagger">
          {items.map((b) => (
            <div key={b.id} className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                    {b.pro_specialty} · {b.modality}
                  </div>
                  <div className="mt-1 font-display text-lg text-[#1F2937]">{b.pro_name}</div>
                  <div className="mt-1 text-xs text-[#5B5B62]">{b.topic_fr}</div>
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                    {(b.proposed_slots || []).map((s) => new Date(s).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { dateStyle: "short", timeStyle: "short" })).join(" · ")}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-widest ${
                  b.status === "confirmed" ? "border-[#059669]/40 bg-[#ECFDF5] text-[#065F46]"
                  : b.status === "cancelled" ? "border-[#DC2626]/40 bg-[#FEF2F2] text-[#991B1B]"
                  : "border-[#C5A880]/40 bg-[#FBF6EA] text-[#7A5B14]"
                }`}>
                  {b.status.replace(/_/g, " ")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
